import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, ArrowRight, Loader2 } from "lucide-react";

// Premium Hero Card Component
const PremiumHeroCard = () => {
  return (
    <div className="premium-hero group mb-8">
      {/* Noise texture overlay */}
      <div className="premium-hero__noise" />
      
      {/* Radial glow behind headline */}
      <div className="premium-hero__glow" />
      
      {/* Main content grid */}
      <div className="premium-hero__content">
        {/* Left column - Main content */}
        <div className="premium-hero__left">
          {/* Premium icon badge */}
          <div className="premium-hero__badge">
            <div className="premium-hero__badge-ring">
              <div className="premium-hero__badge-inner">
                <FileText size={22} strokeWidth={1.5} />
              </div>
            </div>
          </div>
          
          {/* Headline with gradient underline on "3-way match" */}
          <h1 className="premium-hero__title" data-testid="start-run-headline">
            Start a <span className="premium-hero__highlight">3-way match</span> run
          </h1>
          
          {/* Subhead - single concise line */}
          <p className="premium-hero__subtitle">
            Label your audit scope for easy retrieval
          </p>
          
          {/* Example chip */}
          <div className="premium-hero__chip">
            <span className="premium-hero__chip-label">Example:</span>
            <span className="premium-hero__chip-value">Q2 FY26 · Pune Plant · Apr-Jun</span>
          </div>
        </div>
        
        {/* Right column - Decorative motif */}
        <div className="premium-hero__right">
          {/* Abstract data flow visualization */}
          <svg className="premium-hero__motif" viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Dotted curve path */}
            <path 
              d="M20 100 Q60 100 80 60 Q100 20 140 40 Q180 60 180 20" 
              stroke="currentColor" 
              strokeWidth="1.5" 
              strokeDasharray="4 6"
              strokeLinecap="round"
              opacity="0.3"
            />
            {/* Flow nodes */}
            <circle cx="20" cy="100" r="4" fill="currentColor" opacity="0.4" />
            <circle cx="80" cy="60" r="3" fill="currentColor" opacity="0.3" />
            <circle cx="140" cy="40" r="3" fill="currentColor" opacity="0.3" />
            <circle cx="180" cy="20" r="4" fill="currentColor" opacity="0.4" />
          </svg>
          
          {/* Floating chips */}
          <div className="premium-hero__floating-chips">
            <div className="premium-hero__float-chip premium-hero__float-chip--1">
              PO.csv
            </div>
            <div className="premium-hero__float-chip premium-hero__float-chip--2">
              Invoice.xlsx
            </div>
            <div className="premium-hero__float-chip premium-hero__float-chip--3">
              Exception: Mismatch
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function StartRunScreen({ onSubmit, loading }) {
  const [formData, setFormData] = useState({
    requester_name: "",
    scope_label: "",
    notes: ""
  });
  const [error, setError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    
    if (!formData.scope_label.trim()) {
      setError("Scope label is required.");
      return;
    }
    
    onSubmit(formData);
  };

  return (
    <div className="max-w-2xl mx-auto animate-fadeIn">
      {/* Premium Hero Section */}
      <PremiumHeroCard />

      {/* Form Card */}
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="text-xl font-semibold tracking-tight text-[#062B55]">Run Details</CardTitle>
          <CardDescription className="text-slate-500">
            Enter the details for your audit run
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="requester_name" className="form-label">
                Requester Name
              </Label>
              <Input
                id="requester_name"
                data-testid="requester-name-input"
                placeholder="e.g., John Smith"
                value={formData.requester_name}
                onChange={(e) => setFormData({ ...formData, requester_name: e.target.value })}
                className="form-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="scope_label" className="form-label">
                Scope Label <span className="text-red-500">*</span>
              </Label>
              <Input
                id="scope_label"
                data-testid="scope-label-input"
                placeholder="e.g., Q2 FY26 | Pune Plant | Apr–Jun"
                value={formData.scope_label}
                onChange={(e) => {
                  setFormData({ ...formData, scope_label: e.target.value });
                  if (error) setError("");
                }}
                className={`form-input ${error ? "border-red-500 focus:border-red-500 focus:ring-red-200" : ""}`}
              />
              {error && (
                <p className="text-sm text-red-500" data-testid="scope-label-error">{error}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes" className="form-label">
                Notes <span className="text-slate-400">(optional)</span>
              </Label>
              <Textarea
                id="notes"
                data-testid="notes-input"
                placeholder="Add any additional notes for this run..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="form-input min-h-[100px]"
              />
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                data-testid="start-run-button"
                disabled={loading}
                className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    Start run
                    <ArrowRight size={18} />
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Empty State Info */}
      <div className="mt-8 text-center text-sm text-slate-500">
        <p>No runs yet. Start your first run to begin the 3-way match process.</p>
      </div>
    </div>
  );
}
